import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Shield, 
  ShieldOff, 
  ShieldCheck, 
  Ban, 
  RefreshCw,
  Plus,
  Trash2,
  Clock,
  AlertTriangle,
  Settings2,
  Loader2,
  Globe,
  Users,
  Monitor,
  Smartphone
} from "lucide-react";

interface ConnectedIP {
  ip_address: string;
  country: string | null;
  city: string | null;
  device_type: string | null;
  browser: string | null;
  last_seen: string;
  page_count: number;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
}

interface BlockedIP {
  id: string;
  ip_address: string;
  reason: string;
  attempts_count: number;
  blocked_at: string;
  blocked_until: string;
  is_permanent: boolean;
  notes: string | null;
}

interface WhitelistedIP {
  id: string;
  ip_address: string;
  reason: string | null;
  created_at: string;
}

interface Fail2BanConfig {
  id: string;
  attempt_type: string;
  max_attempts: number;
  window_minutes: number;
  ban_duration_minutes: number;
  enabled: boolean;
}

interface Fail2BanStats {
  blocked_ips: BlockedIP[];
  whitelisted_ips: WhitelistedIP[];
  connected_ips: ConnectedIP[];
  attempts_24h: number;
  attempts_by_ip: Record<string, number>;
  config: Fail2BanConfig[];
}

export function Fail2BanManager() {
  const { toast } = useToast();
  const [stats, setStats] = useState<Fail2BanStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [blockDialog, setBlockDialog] = useState(false);
  const [whitelistDialog, setWhitelistDialog] = useState(false);
  const [configDialog, setConfigDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<Fail2BanConfig | null>(null);
  
  const [newBlock, setNewBlock] = useState({
    ip_address: '',
    reason: '',
    duration_minutes: 1440,
    is_permanent: false
  });
  
  const [newWhitelist, setNewWhitelist] = useState({
    ip_address: '',
    reason: ''
  });

  const fetchStats = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fail2ban`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({ action: 'stats' })
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      
      setStats(data);
    } catch (error: any) {
      console.error('Error fetching fail2ban stats:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de charger les statistiques",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleBlock = async () => {
    if (!newBlock.ip_address) return;
    
    try {
      setActionLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fail2ban`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            action: 'block',
            ...newBlock
          })
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      toast({
        title: "IP bloquée",
        description: `${newBlock.ip_address} a été bloquée avec succès`,
      });

      setBlockDialog(false);
      setNewBlock({ ip_address: '', reason: '', duration_minutes: 1440, is_permanent: false });
      fetchStats();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblock = async (ip: string) => {
    try {
      setActionLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fail2ban`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({ action: 'unblock', ip_address: ip })
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      toast({
        title: "IP débloquée",
        description: `${ip} a été débloquée`,
      });

      fetchStats();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleWhitelist = async () => {
    if (!newWhitelist.ip_address) return;
    
    try {
      setActionLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fail2ban`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            action: 'whitelist',
            ...newWhitelist
          })
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      toast({
        title: "IP en liste blanche",
        description: `${newWhitelist.ip_address} ajoutée à la whitelist`,
      });

      setWhitelistDialog(false);
      setNewWhitelist({ ip_address: '', reason: '' });
      fetchStats();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveWhitelist = async (ip: string) => {
    try {
      setActionLoading(true);
      await supabase.from('whitelisted_ips').delete().eq('ip_address', ip);
      toast({
        title: "Supprimé",
        description: `${ip} retirée de la whitelist`,
      });
      fetchStats();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateConfig = async () => {
    if (!selectedConfig) return;
    
    try {
      setActionLoading(true);
      const { error } = await supabase
        .from('fail2ban_config')
        .update({
          max_attempts: selectedConfig.max_attempts,
          window_minutes: selectedConfig.window_minutes,
          ban_duration_minutes: selectedConfig.ban_duration_minutes,
          enabled: selectedConfig.enabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedConfig.id);

      if (error) throw error;

      toast({
        title: "Configuration mise à jour",
        description: `Règle ${selectedConfig.attempt_type} modifiée`,
      });

      setConfigDialog(false);
      fetchStats();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (date: string) => new Date(date) < new Date();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Fail2Ban - Protection contre les attaques
          </h3>
          <p className="text-muted-foreground mt-1">
            Bloque automatiquement les IPs malveillantes après trop de tentatives échouées
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchStats}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button variant="outline" onClick={() => setWhitelistDialog(true)}>
            <ShieldCheck className="h-4 w-4 mr-2" />
            Whitelist
          </Button>
          <Button onClick={() => setBlockDialog(true)}>
            <Ban className="h-4 w-4 mr-2" />
            Bloquer une IP
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <Ban className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">IPs bloquées</p>
                <p className="text-2xl font-bold">{stats?.blocked_ips.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <ShieldCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Whitelist</p>
                <p className="text-2xl font-bold">{stats?.whitelisted_ips.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tentatives (24h)</p>
                <p className="text-2xl font-bold">{stats?.attempts_24h || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Globe className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">IPs uniques (24h)</p>
                <p className="text-2xl font-bold">{Object.keys(stats?.attempts_by_ip || {}).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Onglets */}
      <Tabs defaultValue="connected">
        <TabsList>
          <TabsTrigger value="connected">
            <Users className="h-4 w-4 mr-2" />
            IPs connectées ({stats?.connected_ips?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="blocked">
            <Ban className="h-4 w-4 mr-2" />
            IPs bloquées ({stats?.blocked_ips.length || 0})
          </TabsTrigger>
          <TabsTrigger value="whitelist">
            <ShieldCheck className="h-4 w-4 mr-2" />
            Liste blanche ({stats?.whitelisted_ips.length || 0})
          </TabsTrigger>
          <TabsTrigger value="config">
            <Settings2 className="h-4 w-4 mr-2" />
            Configuration
          </TabsTrigger>
        </TabsList>

        {/* IPs connectées */}
        <TabsContent value="connected" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Visiteurs connectés (dernières 24h)</CardTitle>
              <CardDescription>
                Adresses IP ayant visité le site récemment
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!stats?.connected_ips || stats.connected_ips.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun visiteur enregistré</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Adresse IP</TableHead>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Pays</TableHead>
                      <TableHead>Ville</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Navigateur</TableHead>
                      <TableHead>Pages vues</TableHead>
                      <TableHead>Dernière visite</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.connected_ips.map((ip, idx) => (
                      <TableRow key={`${ip.ip_address}-${idx}`}>
                        <TableCell className="font-mono font-medium">{ip.ip_address}</TableCell>
                        <TableCell>
                          {ip.user_name ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-sm">{ip.user_name}</span>
                              <span className="text-xs text-muted-foreground">{ip.user_email}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm italic">Anonyme</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{ip.country || '-'}</Badge>
                        </TableCell>
                        <TableCell>{ip.city || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                            {ip.device_type === 'mobile' ? (
                              <Smartphone className="h-3 w-3" />
                            ) : (
                              <Monitor className="h-3 w-3" />
                            )}
                            {ip.device_type || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>{ip.browser || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{ip.page_count}</Badge>
                        </TableCell>
                        <TableCell>{formatDate(ip.last_seen)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setNewWhitelist({ ip_address: ip.ip_address, reason: 'Visiteur légitime' });
                                setWhitelistDialog(true);
                              }}
                              title="Ajouter à la whitelist"
                            >
                              <ShieldCheck className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setNewBlock({ ip_address: ip.ip_address, reason: 'Comportement suspect', duration_minutes: 1440, is_permanent: false });
                                setBlockDialog(true);
                              }}
                              title="Bloquer cette IP"
                            >
                              <Ban className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blocked" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>IPs actuellement bloquées</CardTitle>
              <CardDescription>
                Ces adresses IP ne peuvent pas accéder aux services protégés
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.blocked_ips.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune IP bloquée</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Adresse IP</TableHead>
                      <TableHead>Raison</TableHead>
                      <TableHead>Tentatives</TableHead>
                      <TableHead>Bloquée le</TableHead>
                      <TableHead>Expire</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.blocked_ips.map((ip) => (
                      <TableRow key={ip.id}>
                        <TableCell className="font-mono font-medium">{ip.ip_address}</TableCell>
                        <TableCell className="max-w-xs truncate">{ip.reason}</TableCell>
                        <TableCell>{ip.attempts_count}</TableCell>
                        <TableCell>{formatDate(ip.blocked_at)}</TableCell>
                        <TableCell>
                          {ip.is_permanent ? (
                            <Badge variant="destructive">Permanent</Badge>
                          ) : (
                            formatDate(ip.blocked_until)
                          )}
                        </TableCell>
                        <TableCell>
                          {ip.is_permanent ? (
                            <Badge variant="destructive">
                              <Ban className="h-3 w-3 mr-1" />
                              Permanent
                            </Badge>
                          ) : isExpired(ip.blocked_until) ? (
                            <Badge variant="secondary">
                              <Clock className="h-3 w-3 mr-1" />
                              Expiré
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <ShieldOff className="h-3 w-3 mr-1" />
                              Actif
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnblock(ip.ip_address)}
                            disabled={actionLoading}
                          >
                            <ShieldCheck className="h-4 w-4 mr-1" />
                            Débloquer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="whitelist" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Liste blanche</CardTitle>
              <CardDescription>
                Ces adresses IP ne seront jamais bloquées automatiquement
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.whitelisted_ips.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune IP en liste blanche</p>
                  <Button variant="outline" className="mt-4" onClick={() => setWhitelistDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une IP
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Adresse IP</TableHead>
                      <TableHead>Raison</TableHead>
                      <TableHead>Ajoutée le</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.whitelisted_ips.map((ip) => (
                      <TableRow key={ip.id}>
                        <TableCell className="font-mono font-medium">{ip.ip_address}</TableCell>
                        <TableCell>{ip.reason || '-'}</TableCell>
                        <TableCell>{formatDate(ip.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveWhitelist(ip.ip_address)}
                            disabled={actionLoading}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="config" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuration des règles</CardTitle>
              <CardDescription>
                Paramètres de blocage automatique par type de tentative
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Max tentatives</TableHead>
                    <TableHead>Fenêtre (min)</TableHead>
                    <TableHead>Durée ban (min)</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.config.map((cfg) => (
                    <TableRow key={cfg.id}>
                      <TableCell className="font-medium capitalize">{cfg.attempt_type}</TableCell>
                      <TableCell>{cfg.max_attempts}</TableCell>
                      <TableCell>{cfg.window_minutes}</TableCell>
                      <TableCell>{cfg.ban_duration_minutes}</TableCell>
                      <TableCell>
                        <Badge variant={cfg.enabled ? "default" : "secondary"}>
                          {cfg.enabled ? 'Actif' : 'Désactivé'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedConfig(cfg);
                            setConfigDialog(true);
                          }}
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog bloquer IP */}
      <Dialog open={blockDialog} onOpenChange={setBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5" />
              Bloquer une adresse IP
            </DialogTitle>
            <DialogDescription>
              L'IP bloquée ne pourra plus accéder aux services protégés
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Adresse IP</Label>
              <Input
                placeholder="192.168.1.1"
                value={newBlock.ip_address}
                onChange={(e) => setNewBlock(prev => ({ ...prev, ip_address: e.target.value }))}
              />
            </div>
            <div>
              <Label>Raison</Label>
              <Input
                placeholder="Attaque par force brute..."
                value={newBlock.reason}
                onChange={(e) => setNewBlock(prev => ({ ...prev, reason: e.target.value }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Ban permanent</Label>
                <p className="text-xs text-muted-foreground">L'IP restera bloquée indéfiniment</p>
              </div>
              <Switch
                checked={newBlock.is_permanent}
                onCheckedChange={(checked) => setNewBlock(prev => ({ ...prev, is_permanent: checked }))}
              />
            </div>
            {!newBlock.is_permanent && (
              <div>
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  value={newBlock.duration_minutes}
                  onChange={(e) => setNewBlock(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 1440 }))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  1440 min = 24h, 10080 min = 7 jours
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleBlock} disabled={actionLoading || !newBlock.ip_address}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
              Bloquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog whitelist */}
      <Dialog open={whitelistDialog} onOpenChange={setWhitelistDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Ajouter à la liste blanche
            </DialogTitle>
            <DialogDescription>
              Cette IP ne sera jamais bloquée automatiquement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Adresse IP</Label>
              <Input
                placeholder="192.168.1.1"
                value={newWhitelist.ip_address}
                onChange={(e) => setNewWhitelist(prev => ({ ...prev, ip_address: e.target.value }))}
              />
            </div>
            <div>
              <Label>Raison (optionnel)</Label>
              <Input
                placeholder="IP du bureau..."
                value={newWhitelist.reason}
                onChange={(e) => setNewWhitelist(prev => ({ ...prev, reason: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhitelistDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleWhitelist} disabled={actionLoading || !newWhitelist.ip_address}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog config */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Modifier la règle: {selectedConfig?.attempt_type}
            </DialogTitle>
          </DialogHeader>
          {selectedConfig && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Activé</Label>
                  <p className="text-xs text-muted-foreground">Protection active pour ce type</p>
                </div>
                <Switch
                  checked={selectedConfig.enabled}
                  onCheckedChange={(checked) => setSelectedConfig(prev => prev ? ({ ...prev, enabled: checked }) : null)}
                />
              </div>
              <div>
                <Label>Nombre max de tentatives</Label>
                <Input
                  type="number"
                  min={1}
                  value={selectedConfig.max_attempts}
                  onChange={(e) => setSelectedConfig(prev => prev ? ({ ...prev, max_attempts: parseInt(e.target.value) || 5 }) : null)}
                />
              </div>
              <div>
                <Label>Fenêtre de temps (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  value={selectedConfig.window_minutes}
                  onChange={(e) => setSelectedConfig(prev => prev ? ({ ...prev, window_minutes: parseInt(e.target.value) || 15 }) : null)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Période pendant laquelle les tentatives sont comptées
                </p>
              </div>
              <div>
                <Label>Durée du ban (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  value={selectedConfig.ban_duration_minutes}
                  onChange={(e) => setSelectedConfig(prev => prev ? ({ ...prev, ban_duration_minutes: parseInt(e.target.value) || 30 }) : null)}
                />
              </div>
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Après {selectedConfig.max_attempts} tentatives échouées en {selectedConfig.window_minutes} minutes, 
                  l'IP sera bloquée pendant {selectedConfig.ban_duration_minutes} minutes.
                </AlertDescription>
              </Alert>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateConfig} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
